import { EvesButton } from "@/common/components/atoms/button/button";
import { EvesChip } from "@/common/components/atoms/chip/chip";
import { Card } from "@/common/components/molecules/card/card";
import { ITableActionMenuModel } from "@/common/components/organisms/table/actionMenu";
import DataTable from "@/common/components/organisms/table/dataTable";
import { withContext } from "@/common/utils/withContext";
import {
  getBillingAccounts,
  onboardAccount,
  onDeleteAccount,
  resendConnectedAccountEmail,
} from "@/services/integrationSettings";
import { Add, Business, Delete, PersonAdd, Refresh } from "@mui/icons-material";
import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import AddConnectedAccount from "../addConnectedAccount";
import { NotifyType } from "@/common/components/molecules/notification/notification";
import { BillingAccountStatus, RestResponse } from "@/common/enums/enums";
import { useTranslation } from "react-i18next";
import { DefaultPageSize } from "@/common/constants/constants";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import store from "@/store";
import { setLoader } from "@/store/app/reducer";
import { useSearchParams } from "react-router-dom";
const ConnectedAccounts = (props: any) => {
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const { t: translate } = useTranslation();
  const [state, setStateData] = useState({
    rows: [],
    totalRowsCount: 0,
    sortDescending: false,
    sortFields: "companyName",
    page: 0,
    limit: DefaultPageSize,
  } as any);

  const { refetch: fetchBillingAccounts } = useQuery(
    "Billing Accounts",
    () => {
      return getBillingAccounts({
        Limit: state.limit,
        SortFields: state.sortFields,
        Page: state.page + 1,
        ...(statusParam && { Status: statusParam }),
      });
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: async (data: any) => {
        if (data) {
          const connectedAccounts: any[] = [];
          data.result.forEach((connectedAccount: any) => {
            connectedAccounts.push({
              ...connectedAccount,
              fullName: connectedAccount.businessOwner
                ? connectedAccount.businessOwner.firstName + " " + connectedAccount.businessOwner.name
                : "-",
              email: connectedAccount.businessOwner ? connectedAccount.businessOwner.email : "-",
            });
          });
          setStateData((currentData: any) => {
            return {
              ...currentData,
              rows: connectedAccounts,
              totalRowsCount: data?.count,
            };
          });
        }
      },
    },
  );

  const { mutate: onboardAccountAsync } = useMutation((accountID: any) => onboardAccount(accountID), {
    onMutate: () => {
      store.dispatch(setLoader(true));
    },
    onSettled: () => {
      store.dispatch(setLoader(false));
    },
    onSuccess: (data: any) => {
      const notify: NotifyType = {
        message: "",
        type: "success",
      };
      if (data.status === "Success") {
        notify.message = `${translate("accounts.message.onboard_success")}`;
        notify.type = "success";
        fetchBillingAccounts();
        props.notificationService?.notify(notify);
        props.onFormClose();
        props.fetchAllConnectedAccounts();
      } else {
        notify.message = `${translate("accounts.message.onboard_error")}`;
        notify.type = "error";
        props.notificationService?.notify(notify);
        props.onFormClose();
        props.fetchAllConnectedAccounts();
      }
    },
  });

  const accountOnboard = (record: any) => {
    onboardAccountAsync(record.id);
  };

  const primaryActions: ITableActionMenuModel[] = [
    {
      title: () => `${translate("general.resend_email")}`,
      icon: () => ScheduleSendIcon,
      iconColor: "primary",
      visible: (record: any) =>
        [BillingAccountStatus.PENDING, BillingAccountStatus.INCOMPLETE].includes(record.status) ? true : false,
      onClick: (record: any) => resendParticularRecord(record),
    },
    {
      title: () => translate("accounts.onboarding.onboard_action"),
      icon: () => PersonAdd,
      iconColor: "primary",
      onClick: accountOnboard,
      visible: (record: any) => (record.status == BillingAccountStatus.IDLE ? true : false),
    },
    {
      title: () => `${translate("general.delete")}`,
      icon: () => Delete,
      iconColor: "error",
      visible: (record: any) =>
        [BillingAccountStatus.IDLE, BillingAccountStatus.PENDING].includes(record.status) ? true : false,
      onClick: (record: any) => deleteParticularRecord(record),
    },
    {
      title: () => "",
      icon: () => "",
      iconColor: "",
      visible: () => true,
      disabled: () => true,
    },
  ];

  const resendParticularRecord = (record: any) => {
    props.dialogService?.showConfirm(
      {
        title: `${translate("Resend Email")}`,
        description: `${translate("Are you sure you want to resend the connected account setup email?")}`,
        confirmType: "CONNECTED_ACCOUNT",
      },
      async (result: any) => {
        if (result === "YES") {
          try {
            const response: any = await resendConnectedAccountEmail(record.id);

            if (response?.status === "Success") {
              props.notificationService?.notify({
                message: response?.message || "Reminder email has been sent successfully",
                type: "success",
              });
            } else {
              props.notificationService?.notify({
                message: response?.message || "Failed to send email. Please check logs and try again.",
                type: "error",
              });
            }
          } catch (err) {
            props.notificationService?.notify({
              message: "Failed to send email. Please check logs and try again.",
              type: "error",
            });
          }
        }
      },
    );
  };

  const deleteParticularRecord = (record: any) => {
    props.dialogService?.showConfirm(
      {
        title: `${translate("Delete")}`,
        description: `${translate("Are you sure you want to delete the connected account?")}`,
        confirmType: "CONNECTED_ACCOUNT",
      },
      (result: any) => {
        if (result === "YES") {
          //Delete connected account
          onDeleteAccount(record.id).then(
            (response: any) => {
              if (response.status === RestResponse.SUCCESS) {
                props.notificationService?.notify({
                  message: "The connected Stripe account has been deleted successfully.",
                  type: "success",
                });
                fetchBillingAccounts();
              } else {
                props.notificationService?.notify({
                  message: "Error deleting connected account, Please try again",
                  type: "error",
                });
              }
            },
            (error: any) => {
              if (error.status === 537) {
                props.notificationService?.notify({
                  message: "Error deleting connected account, Please try again",
                  type: "error",
                });
              } else {
                props.notificationService?.notify({
                  message: "Error deleting connected account, Please try again",
                  type: "error",
                });
              }
            },
          );
        }
      },
    );
  };

  const Status = (row: any) => {
    if (row.status === "pending") {
      return <EvesChip label={`${translate("accounts.status.account_pending")}`} type={"Pending"} />;
    } else if (row.status === "incomplete") {
      return <EvesChip label={`${translate("accounts.status.account_incomplete")}`} type={"Warning"} />;
    } else if (row.status === "idle") {
      return <EvesChip label={`${translate("accounts.status.account_idle")}`} type={"Faulted"} />;
    } else {
      return <EvesChip label={`${translate("accounts.status.account_active")}`} type={"Available"} />;
    }
  };

  const columns = [
    {
      id: "actions",
      label: `${translate("general.actions")}`,
      minWidth: 20,
    },
    {
      id: "status",
      label: `${translate("settings.billing.payment_methods_status")}`,
      textAlign: "center",
      sortKey: "status",
      minWidth: 70,
      maxWidth: 240,
      allowSort: true,
    },
    {
      id: "companyName",
      label: `${translate("accounts.list.company_name")}`,
      textAlign: "center",
      allowSort: true,
      sortKey: "companyName",
      minWidth: 50,
      maxWidth: 210,
    },
    {
      id: "fullName",
      label: `${translate("accounts.list.business_owner")}`,
      textAlign: "center",
      allowSort: true,
      sortKey: "businessOwner.name",
      minWidth: 100,
      maxWidth: 210,
    },
    {
      id: "email",
      label: `${translate("users.email")}`,
      allowSort: true,
      sortKey: "businessOwner.email",
      textAlign: "center",
      minWidth: 120,
      maxWidth: 210,
    },
    {
      id: "accountExternalID",
      label: `${translate("accounts.list.account_id")}`,
      allowSort: true,
      sortKey: "accountExternalID",
      textAlign: "center",
      minWidth: 100,
      maxWidth: 210,
    },
  ];

  const onFormClose = () => {
    setStateData((currentData: any) => {
      return {
        ...currentData,
        showConnectedAccountForm: false,
      };
    });
  };

  const resetConnectedAccountForm = (val: any) => {
    setStateData((currentData: any) => {
      return {
        ...currentData,
        connectdAccountId: val,
      };
    });
  };

  const createPaymentMethod = () => {
    setStateData((currentData: any) => {
      return {
        ...currentData,
        showConnectedAccountForm: true,
      };
    });
  };

  useEffect(() => {
    fetchBillingAccounts();
  }, [state.sortFields, state.limit, state.page, statusParam]);

  const onDataTableChange = (params: any) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        sortFields: params.sortFields,
      };
    });
  };

  const onPageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        page: newPage,
      };
    });
  };
  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        page: 0,
        limit: parseInt(event.target.value),
      };
    });
  };

  return (
    <Card icon={<Business />} title={`${translate("accounts.title")}`} isTransparent={true}>
      <>
        <Stack direction={"row"} justifyContent={"space-between"} marginBottom={"10px"}>
          <EvesButton
            startIcon={<Add style={{ fontSize: "22.4px" }} />}
            data-cy="connected-accounts-create-btn"
            onClick={createPaymentMethod}
            style={{ padding: "8px 24px" }}
          >
            {`${translate("general.create")}`}
          </EvesButton>
          <EvesButton data-cy="payment-refresh-btn" onClick={fetchBillingAccounts}>
            <Refresh />
          </EvesButton>
        </Stack>
        <DataTable
          className="companies-table"
          data-cy="companies-table"
          rows={state.rows}
          columns={columns}
          actionsMenu={primaryActions}
          totalRowsCount={state.totalRowsCount}
          onChange={onDataTableChange}
          defaultSortField={state.sortFields}
          height={{ height: "calc(70vh - 328px)" }}
          count={state.totalRowsCount}
          limit={state.limit}
          page={state.page}
          tablePagination={true}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        >
          <Status slot="status" />
        </DataTable>
        {state.showConnectedAccountForm && (
          <AddConnectedAccount
            showConnectedAccountForm={state.showConnectedAccountForm}
            resetConnectedAccountForm={resetConnectedAccountForm}
            onFormClose={onFormClose}
            billingData={props.billingData}
            fetchAllConnectedAccounts={fetchBillingAccounts}
            canCreate={state.canCreate}
          />
        )}
      </>
    </Card>
  );
};

export default withContext(ConnectedAccounts);
